import { useEffect, useRef, useState } from 'react';
import Container from '@/components/common/elements/Container';
import StyledBox from '@/components/common/elements/Box';
import ChipButton from '@/components/common/elements/ChipButton';
import { SubwayStation } from '@/api/repository/types/types';
import Text from '@/components/common/elements/Text';

// 네이버 지도 API 클라이언트 ID - 환경변수로 관리하는 것을 권장합니다
// Naver Cloud Platform(https://www.ncloud.com/)에서 발급받아 사용하세요
const NAVER_MAP_CLIENT_ID = import.meta.env.VITE_NAVER_MAP_CLIENT_ID || 'r6v8jo6hjz';

interface NaverMapMarkerProps {
  x: number;
  y: number;
  chipList: SubwayStation[];
  /** 지도 고정 — 드래그·휠·핀치·더블탭 확대·키보드를 모두 끈다(모바일에서 지도 위 세로 스크롤이 페이지로 넘어간다) */
  fixed?: boolean;
}

const MAP_SCRIPT_ID = 'naver-map-sdk';

/** 네이버 지도 SDK 를 한 번만 불러온다(이미 불러오는 중이면 그 로드를 기다린다) */
const loadNaverMapSdk = (onReady: () => void): (() => void) => {
  if ((window as any).naver?.maps) {
    onReady();
    return () => undefined;
  }
  let script = document.getElementById(MAP_SCRIPT_ID) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.id = MAP_SCRIPT_ID;
    script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${NAVER_MAP_CLIENT_ID}`;
    script.async = true;
    script.onerror = () => console.error('Failed to load Naver Map API');
    document.head.appendChild(script);
  }
  const handleLoad = () => onReady();
  script.addEventListener('load', handleLoad);
  return () => script?.removeEventListener('load', handleLoad);
};

const FIXED_MAP_OPTIONS = {
  draggable: false,
  scrollWheel: false,
  pinchZoom: false,
  keyboardShortcuts: false,
  disableDoubleClickZoom: true,
  disableDoubleTapZoom: true,
  disableTwoFingerTapZoom: true,
  zoomControl: false,
};

const NaverMap = ({ x, y, chipList, fixed = false }: NaverMapMarkerProps) => {
  const mapElement = useRef<HTMLDivElement | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // 화면을 떠난 뒤 로드가 끝나도 setState 하지 않는다(로드 중 이탈 시 pageerror 방지)
    let alive = true;
    const unsubscribe = loadNaverMapSdk(() => { if (alive) setIsLoaded(true); });
    return () => {
      alive = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    const naver = (window as any).naver;
    const element = mapElement.current;
    if (!naver || !naver.maps || !element) return;

    const map = new naver.maps.Map(element, {
      center: new naver.maps.LatLng(x, y),
      zoom: 16,
      ...(fixed ? FIXED_MAP_OPTIONS : {}),
    });

    const marker = new naver.maps.Marker({
      position: new naver.maps.LatLng(x, y),
      map: map,
    });

    // 언마운트·좌표 변경 시 지도를 정리한다(떠난 뒤 내부 타이머가 사라진 DOM 을 건드리지 않게)
    return () => {
      try {
        marker.setMap(null);
        map.destroy?.();
      } catch {
        // 이미 정리됨
      }
    };
  }, [x, y, isLoaded, fixed]);
  
  const isChipList = chipList && chipList?.length > 0
  const borderStyles = isChipList? "0px" : "10px"

  return (
    <Container display='block' width='100%' maxwidth='100%'>
      <div ref={mapElement} style={{
        width: '100%',
        height: 'clamp(200px, 40vw, 300px)',
        margin:'clamp(10px, 2.5vw, 20px) 0 0 0',
        borderRadius:'10px',
        borderBottomRightRadius:borderStyles,
        borderBottomLeftRadius:borderStyles,
        boxSizing: 'border-box',
        // 고정 지도는 터치를 가로채지 않는다 — 세로 스크롤이 페이지로 넘어간다
        touchAction: fixed ? 'pan-y' : undefined,
      }} />
      {isChipList &&
        <StyledBox
          boxposition='bottom'
          borderradius='10px'
          display='flex'
          padding='clamp(8px, 1.5vw, 12px)'
          minheight='auto'
          margin='clamp(10px, 2.5vw, 20px) 0 0 0'
        >
          <Text
            margin='0 clamp(8px, 1.5vw, 10px) 0 0'
            aligncontent='center'
            fontweight='bold'
            minwidth='fit-content'
          >
            인근지하철
          </Text>
          <Container
            display='flex'
            gap='clamp(3px, 0.8vw, 5px)'
            width='100%'
            flexwrap='wrap'
          >
          {chipList?.map((item) => (
            <ChipButton key={item.id} label={item.name}/>
          ))}
          </Container>
        </StyledBox>
      }
    </Container>

  );
};

export default NaverMap;
